import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Loader2, UploadCloud, Trash2, Save, Eye, EyeOff, Images, ArrowLeftRight } from "lucide-react";
import { api, API } from "@/lib/api";

const img = (id) => `${API}/public-image/${id}`;
const inputCls = "w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brandgreen";

export function GalleryManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [pair, setPair] = useState({ before: null, after: null, title: "" });
  const [pairBusy, setPairBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/gallery").then(({ data }) => setItems(data || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await api.post("/admin/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return res.data.file_id;
  };

  const handleSingleUpload = async (files) => {
    const list = Array.from(files);
    if (!list.length) return;
    setUploading(true);
    for (const f of list) {
      try {
        const fid = await uploadOne(f);
        await api.post("/admin/gallery", { type: "single", after_file_id: fid, title: "", caption: "", is_published: true });
      } catch { toast.error(`${f.name}: upload failed`); }
    }
    setUploading(false);
    toast.success("Photos added to gallery");
    load();
  };

  const uploadPairSlot = async (slot, file) => {
    try {
      const fid = await uploadOne(file);
      setPair((p) => ({ ...p, [slot]: fid }));
    } catch { toast.error("Upload failed"); }
  };

  const createPair = async () => {
    if (!pair.before || !pair.after) return toast.error("Add both a before and after photo.");
    setPairBusy(true);
    try {
      await api.post("/admin/gallery", { type: "pair", before_file_id: pair.before, after_file_id: pair.after, title: pair.title, caption: "", is_published: true });
      toast.success("Before/After added");
      setPair({ before: null, after: null, title: "" });
      load();
    } catch { toast.error("Could not create pair"); } finally { setPairBusy(false); }
  };

  const saveItem = async (it) => {
    try {
      await api.patch(`/admin/gallery/${it.id}`, {
        type: it.type, title: it.title || "", caption: it.caption || "",
        before_file_id: it.before_file_id, after_file_id: it.after_file_id, is_published: it.is_published,
      });
      toast.success("Saved");
    } catch { toast.error("Could not save"); }
  };

  const togglePublish = async (it) => {
    const next = { ...it, is_published: !it.is_published };
    setItems((arr) => arr.map((x) => (x.id === it.id ? next : x)));
    try { await api.patch(`/admin/gallery/${it.id}`, { type: next.type, title: next.title || "", caption: next.caption || "", before_file_id: next.before_file_id, after_file_id: next.after_file_id, is_published: next.is_published }); }
    catch { toast.error("Could not update"); load(); }
  };

  const del = async (id) => {
    setItems((arr) => arr.filter((x) => x.id !== id));
    try { await api.delete(`/admin/gallery/${id}`); toast.success("Removed"); }
    catch { toast.error("Could not remove"); load(); }
  };

  const setField = (id, k, v) => setItems((arr) => arr.map((x) => (x.id === id ? { ...x, [k]: v } : x)));

  return (
    <div className="space-y-6">
      {/* Uploaders */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-gray-100 bg-white p-5">
          <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><Images className="w-4 h-4 text-brandgreen" /> Add work photos</h3>
          <p className="text-sm text-gray-500 mt-1">Upload single photos of completed jobs.</p>
          <div onClick={() => fileRef.current?.click()} className="mt-4 cursor-pointer rounded-xl border-2 border-dashed border-gray-200 hover:border-brandgreen/50 bg-neutralbg/50 p-6 text-center transition-colors" data-testid="gallery-upload-zone">
            {uploading ? <Loader2 className="w-6 h-6 animate-spin text-navy mx-auto" /> : <UploadCloud className="w-7 h-7 text-brandgreen mx-auto" />}
            <p className="mt-2 text-sm font-medium text-navy">Click to upload images</p>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleSingleUpload(e.target.files)} data-testid="gallery-file-input" />
          </div>
        </div>

        <div className="rounded-2xl border border-gray-100 bg-white p-5">
          <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><ArrowLeftRight className="w-4 h-4 text-brandgreen" /> Add before / after pair</h3>
          <p className="text-sm text-gray-500 mt-1">Show a transformation with a slider.</p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            {["before", "after"].map((slot) => (
              <label key={slot} className="cursor-pointer rounded-xl border-2 border-dashed border-gray-200 hover:border-brandgreen/50 bg-neutralbg/50 aspect-square grid place-items-center overflow-hidden relative" data-testid={`pair-${slot}`}>
                {pair[slot] ? <img src={img(pair[slot])} alt={slot} className="w-full h-full object-cover" /> : <span className="text-xs font-semibold text-gray-400 uppercase">{slot}</span>}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadPairSlot(slot, e.target.files[0])} />
              </label>
            ))}
          </div>
          <input className={`${inputCls} mt-3`} placeholder="Title (optional)" value={pair.title} onChange={(e) => setPair((p) => ({ ...p, title: e.target.value }))} />
          <button onClick={createPair} disabled={pairBusy} className="mt-3 inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-dark transition-colors disabled:opacity-50" data-testid="create-pair-btn">
            {pairBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowLeftRight className="w-4 h-4" />} Add pair
          </button>
        </div>
      </div>

      {/* Items */}
      {loading ? (
        <div className="h-32 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-12 text-center text-gray-400">No gallery items yet. Upload some work photos above.</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => (
            <div key={it.id} className={`rounded-2xl border bg-white overflow-hidden ${it.is_published ? "border-gray-100" : "border-amber-200 bg-amber-50/30"}`} data-testid={`gallery-admin-${it.id}`}>
              <div className="relative aspect-[4/3] bg-neutralbg">
                <img src={img(it.after_file_id || it.before_file_id)} alt={it.title || "work"} className="w-full h-full object-cover" />
                {it.type === "pair" && <span className="absolute top-2 left-2 rounded-full bg-navy/80 text-white text-[10px] font-semibold px-2 py-0.5">Before/After</span>}
              </div>
              <div className="p-3 space-y-2">
                <input className={inputCls} placeholder="Title" value={it.title || ""} onChange={(e) => setField(it.id, "title", e.target.value)} />
                <input className={inputCls} placeholder="Caption" value={it.caption || ""} onChange={(e) => setField(it.id, "caption", e.target.value)} />
                <div className="flex items-center gap-2">
                  <button onClick={() => saveItem(it)} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-brandgreen/10 text-brandgreen-dark px-3 py-2 text-sm font-semibold hover:bg-brandgreen hover:text-white transition-colors"><Save className="w-4 h-4" /> Save</button>
                  <button onClick={() => togglePublish(it)} title={it.is_published ? "Published" : "Hidden"} className="grid place-items-center w-9 h-9 rounded-lg border border-gray-200 text-navy hover:bg-neutralbg">{it.is_published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                  <button onClick={() => del(it.id)} className="grid place-items-center w-9 h-9 rounded-lg border border-gray-200 text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
